import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Send,
} from "lucide-react";

const META = {
  deposit: { Icon: ArrowDownToLine, label: "Deposit", color: "text-[#264C39]", bg: "bg-[#264C39]/15" },
  withdraw: { Icon: ArrowUpFromLine, label: "Withdraw", color: "text-[#B58957]", bg: "bg-[#B58957]/15" },
  transfer_out: { Icon: Send, label: "Transfer", color: "text-[#B58957]", bg: "bg-[#B58957]/15" },
};

function relTime(secs) {
  if (secs == null) return "just now";
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function fmt$(n) {
  if (Number.isInteger(n)) return `$${n.toLocaleString()}`;
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * RecentActivity
 *
 * Kinetic feed underneath the Money-in-Motion ticker. Polls /api/stats/recent_events
 * every 5s, displays at most 6 rows, and animates row insertion/removal so new
 * events feel live.
 */
export default function RecentActivity({ limit = 8, intervalMs = 5000 }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const fetchEvents = async () => {
      try {
        const { data } = await api.get(`/stats/recent_events?limit=${limit}`);
        if (cancelled) return;
        setEvents(data.events || []);
      } catch { /* silent */ }
      finally {
        if (!cancelled) timer = setTimeout(fetchEvents, intervalMs);
      }
    };
    fetchEvents();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [limit, intervalMs]);

  return (
    <div className="mt-8 rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid="recent-activity">
      <div className="px-5 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/60">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B58957] opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#B58957]" />
          </span>
          Live · Recent activity
        </div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40" data-testid="recent-activity-count">
          last {events.length}
        </span>
      </div>
      <ul className="divide-y divide-white/10" data-testid="recent-activity-list">
        {events.length === 0 ? (
          <li className="px-5 py-6 text-center text-sm text-white/50">No activity yet — be the first.</li>
        ) : (
          <AnimatePresence initial={false}>
            {events.slice(0, 6).map((e, i) => {
              const m = META[e.type] || META.deposit;
              const Icon = m.Icon;
              const key = `${e.type}-${e.from_mask}-${e.to_mask || "none"}-${e.amount}-${e.seconds_ago}-${i}`;
              return (
                <motion.li
                  key={key}
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: 8, height: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="px-5 py-3 flex items-center gap-4"
                  data-testid={`recent-activity-row-${i}`}
                >
                  <div className={`w-9 h-9 rounded-lg ${m.bg} ${m.color} flex items-center justify-center shrink-0`}>
                    <Icon className="w-4 h-4" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="text-white truncate">
                      {e.type === "transfer_out" ? (
                        <>
                          <span className="font-mono text-white/85">{e.from_mask}</span>
                          <span className="text-white/40 mx-2">→</span>
                          <span className="font-mono text-white/85">{e.to_mask || "•••???"}</span>
                        </>
                      ) : (
                        <>
                          <span className="text-white/60">{m.label}</span>
                          <span className="text-white/40 mx-2">·</span>
                          <span className="font-mono text-white/85">{e.from_mask}</span>
                        </>
                      )}
                    </div>
                    <div className="text-white/40 text-[11px] uppercase tracking-[0.12em] mt-0.5">
                      {m.label} · {e.mode} · {relTime(e.seconds_ago)}
                    </div>
                  </div>
                  <div className={`font-mono text-base tabular-nums ${m.color}`}>{fmt$(e.amount)}</div>
                </motion.li>
              );
            })}
          </AnimatePresence>
        )}
      </ul>
    </div>
  );
}
